import React from "react";

// Las prestaciones reclamadas estan ligadas 1 a 1 a columnas fijas en la
// base de datos (ver tribunal.py / junta.py), asi que el catalogo de
// Configuracion no puede agregar prestaciones nuevas de la nada: solo
// controla la etiqueta y el orden de estas 12 ya existentes. Una entrada
// del catalogo que no coincida con ninguna de estas etiquetas por defecto
// simplemente no tiene casilla donde guardarse y se ignora.
// Quita acentos/diacriticos antes de comparar, para tolerar variantes bien
// formadas (ej. texto ya normalizado o compuesto con marcas combinantes).
const normalizeLabel = (s) =>
  (s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// Algunos registros en produccion llegan con el caracter acentuado
// eliminado por completo (ej. "Antigedad" en vez de "Antigüedad", "das" en
// vez de "días") por un problema de encoding al guardarse, no solo con el
// diacritico separado. normalizeLabel no puede recuperar eso porque la
// letra base tambien desaparecio, asi que además comparamos "squasheando"
// (quitando del todo) las letras acentuadas conocidas de ambos lados.
const squashLabel = (s) => normalizeLabel(s).replace(/[áéíóúüñ]/g, "");

const DEFAULT_PRESTACIONES_LIST = [
  ["prima_antiguedad", "Prima de Antigüedad"],
  ["salarios_caidos", "Salarios Vencidos"],
  ["vacaciones", "Vacaciones"],
  ["prima_vacacional", "Prima Vacacional"],
  ["aguinaldo", "Aguinaldo"],
  ["reparto_utilidades", "Reparto de Utilidades"],
  ["horas_extras", "Horas Extras"],
  ["dias_90_salario", "Indemnización Const. (90 DÍAS DE SALARIO)"],
  ["dias_20_salario", "Indemnización art. 50 (20 días por año)"],
  ["reinstalacion", "Reinstalacion"],
  ["comisiones", "Comisiones"],
  ["incripcion_retroactiva_imss", "Incripcion Retroactiva al IMSS"],
];

// Mapa por forma normalizada (acentos correctos o NFD) y por forma
// "squasheada" (tolera acentos eliminados por completo), ambos derivados
// de las 12 etiquetas por defecto para no duplicar datos.
const PRESTACIONES_KEY_BY_NORMALIZED = {};
const PRESTACIONES_KEY_BY_SQUASHED = {};
DEFAULT_PRESTACIONES_LIST.forEach(([key, label]) => {
  PRESTACIONES_KEY_BY_NORMALIZED[normalizeLabel(label)] = key;
  PRESTACIONES_KEY_BY_SQUASHED[squashLabel(label)] = key;
});

export default function AccionesPrestacionesSection({
  fieldMeta,
  form,
  errors,
  setField,
  setPrestacion,
  modoRapido = false,
  accionesOptions = [],
  prestacionesCatalogo = null,
}) {
  // Si el catalogo todavia no cargo (null), se muestran las 12 de
  // siempre para no dejar la seccion vacia mientras llega la respuesta.
  const prestacionesList = prestacionesCatalogo
    ? prestacionesCatalogo
        .map((nombre) => {
          const key =
            PRESTACIONES_KEY_BY_NORMALIZED[normalizeLabel(nombre)] ||
            PRESTACIONES_KEY_BY_SQUASHED[squashLabel(nombre)];
          return key ? [key, nombre] : null;
        })
        .filter(Boolean)
    : DEFAULT_PRESTACIONES_LIST;
  const chip = (key, manual = false) => {
    if (manual) return <span className="oa-chip-manual">MANUAL</span>;
    const m = fieldMeta?.(key);
    if (!m) return null;
    if (m.needsReview) return <span className="oa-tag-review" title={m.reviewReason}><span className="material-symbols-outlined" style={{ fontSize: 11, verticalAlign: "middle" }}>warning</span> REVISAR</span>;
    if (m.isAI) return <span className="oa-chip-ai">IA</span>;
    return null;
  };
  const fw = (key) => `oa-field${fieldMeta?.(key)?.needsReview ? " oa-field-review" : ""}`;

  return (
    <section className="oa-card">
      <div className="oa-card-head">
        <h2 className="oa-card-title">
          <span className="oa-card-icon">
            <i className="fa-solid fa-scale-balanced" />
          </span>
          ACCIONES Y PRESTACIONES RECLAMADAS
        </h2>
      </div>

      <div className="oa-card-body">
        <div className="oa-grid-3 oa-mb-8">
          <div className={fw("accion_intentada")}>
            <label className="oa-label">
              <span className="oa-req">*</span> Acción Intentada {chip("accion_intentada")}
            </label>
            <select
              className={`oa-input ${errors.accion_intentada ? "oa-input-error" : ""}`}
              value={form.accion_intentada}
              onChange={(e) => setField("accion_intentada", e.target.value)}
            >
              <option value="">Seleccionar acción</option>
              {(form.accion_intentada && !accionesOptions.includes(form.accion_intentada)
                ? [form.accion_intentada, ...accionesOptions]
                : accionesOptions
              ).map((op) => (
                <option key={op} value={op}>
                  {op}
                </option>
              ))}
            </select>

            {errors.accion_intentada ? <div className="oa-error">{errors.accion_intentada}</div> : null}
          </div>
        </div>

        {!modoRapido && (
        <div className="oa-sep-top">
          <h3 className="oa-subtitle-row">
            <span className="material-symbols-outlined oa-sub-ico">payments</span>
            Prestaciones Reclamadas por el Actor {chip("prestaciones")}
          </h3>

          <div className="oa-checkgrid">
            {prestacionesList.map(([key, label]) => (
              <label className="oa-check" key={key}>
                <input
                  type="checkbox"
                  checked={!!form.prestaciones[key]}
                  onChange={(e) => setPrestacion(key, e.target.checked)}
                />
                <span>{label}</span>
              </label>
            ))}
          </div>

          <div className={`oa-field oa-mt-4${fieldMeta?.("otras_prestaciones")?.needsReview ? " oa-field-review" : ""}`}>
            <label className="oa-label">
              Otras Prestaciones y Detalles {chip("otras_prestaciones", true)}
            </label>
            <textarea
              className={`oa-input`}
              value={form.otras_prestaciones}
              onChange={(e) => setField("otras_prestaciones", e.target.value)}
            />
          </div>
        </div>
        )}
      </div>
    </section>
  );
}
